import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils";

import { Tooltip as TooltipPrimitive } from "@base-ui/react/tooltip";
import { createContext, useContext, useEffect, useState } from "react";

const TooltipModeContext = createContext(false);

function TooltipProvider({ delay = 0, delayDuration, ...props }) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delay={delayDuration ?? delay}
      {...props}
    />
  );
}

function Tooltip({ ...props }) {
  const [isTouchDevice, setIsTouchDevice] = useState(
    () =>
      typeof window.matchMedia === "function" &&
      window.matchMedia("(pointer: coarse)").matches
  );

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(pointer: coarse)");
    const update = () => setIsTouchDevice(media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return (
    <TooltipModeContext.Provider value={isTouchDevice}>
      {isTouchDevice ? (
        <Popover data-slot="tooltip" {...props} />
      ) : (
        <TooltipPrimitive.Root data-slot="tooltip" {...props} />
      )}
    </TooltipModeContext.Provider>
  );
}

function TooltipTrigger({ ...props }) {
  const isTouchDevice = useContext(TooltipModeContext);
  if (isTouchDevice)
    return <PopoverTrigger data-slot="tooltip-trigger" {...props} />;
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  side = "top",
  sideOffset = 4,
  align = "center",
  alignOffset = 0,
  children,
  ...props
}) {
  const isTouchDevice = useContext(TooltipModeContext);
  const contentClassName = cn(
    "bg-text-primary text-primary-foreground z-[115] w-fit max-w-[calc(100vw-2rem)] rounded-md px-3 py-1.5 text-xs data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95",
    className
  );

  if (isTouchDevice) {
    return (
      <PopoverContent
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        className={contentClassName}
        {...props}
      >
        {children}
      </PopoverContent>
    );
  }

  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Positioner
        align={align}
        alignOffset={alignOffset}
        side={side}
        sideOffset={sideOffset}
        className="isolate z-[115]"
      >
        <TooltipPrimitive.Popup
          data-slot="tooltip-content"
          className={cn(
            contentClassName,
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 origin-(--transform-origin)"
          )}
          {...props}
        >
          {children}
        </TooltipPrimitive.Popup>
      </TooltipPrimitive.Positioner>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
